import React, { forwardRef } from 'react';

/**
 * Plain text-input shell that mirrors the design package's input styling
 * (10px radius, 1px border, primary focus ring, dark-mode aware via tokens).
 *
 * forwardRef + spread of all standard input props means this works as a
 * drop-in `<input>` AND as a child of antd v3's `getFieldDecorator(...)
 * (<Input />)` — antd injects value/onChange through the ref.
 *
 * Pass `leadingIcon` / `trailingIcon` to render content inside the field.
 * For multi-line, use `as="textarea"` (defaults rows to 3).
 */
const Input = forwardRef(function Input(
  {
    as = 'input',
    type = 'text',
    error = false,
    leadingIcon,
    trailingIcon,
    className = '',
    rows,
    ...rest
  },
  ref
) {
  const Element = as === 'textarea' ? 'textarea' : 'input';
  const fieldClasses = [
    'w-full bg-surface text-fg placeholder:text-fg-subtle',
    'border rounded-md transition-colors',
    'focus:outline-none focus:ring-2',
    error
      ? 'border-error focus:border-error focus:ring-error/30'
      : 'border-border-default focus:border-primary focus:ring-primary/30',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    leadingIcon ? 'pl-10' : 'pl-3',
    trailingIcon ? 'pr-10' : 'pr-3',
    Element === 'textarea' ? 'py-2 leading-5' : 'h-10 py-2',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (!leadingIcon && !trailingIcon) {
    return (
      <Element
        ref={ref}
        type={Element === 'textarea' ? undefined : type}
        rows={Element === 'textarea' ? (rows ?? 3) : undefined}
        className={fieldClasses}
        {...rest}
      />
    );
  }

  return (
    <div className="relative">
      {leadingIcon ? (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none">
          {leadingIcon}
        </div>
      ) : null}
      <Element
        ref={ref}
        type={Element === 'textarea' ? undefined : type}
        rows={Element === 'textarea' ? (rows ?? 3) : undefined}
        className={fieldClasses}
        {...rest}
      />
      {trailingIcon ? (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-subtle">
          {trailingIcon}
        </div>
      ) : null}
    </div>
  );
});

export default Input;
